import { useRef, useState, useCallback } from 'react';

const OUTPUT_WIDTH = 1400;
const VIEW_W = 420;

export default function AdminImageCropModal({ src, aspect = 7 / 4, onConfirm, onCancel }) {
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [coverScale, setCoverScale] = useState(1);   // 프레임을 빈틈없이 채우는 배율(기본값)
  const [containScale, setContainScale] = useState(1); // 이미지 전체가 보이는 최소 배율(축소 한계)
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  const viewH = Math.round(VIEW_W / aspect);

  // 이미지가 프레임보다 작아진 축은 가운데 정렬, 큰 축만 드래그로 이동 가능
  const clampPos = useCallback((x, y, z, w = natural.w, h = natural.h) => {
    const scaledW = w * z, scaledH = h * z;
    const nx = scaledW <= VIEW_W ? (VIEW_W - scaledW) / 2 : Math.max(VIEW_W - scaledW, Math.min(0, x));
    const ny = scaledH <= viewH ? (viewH - scaledH) / 2 : Math.max(viewH - scaledH, Math.min(0, y));
    return { x: nx, y: ny };
  }, [natural, viewH]);

  const handleImgLoad = () => {
    const el = imgRef.current;
    const w = el.naturalWidth, h = el.naturalHeight;
    const cover   = Math.max(VIEW_W / w, viewH / h);
    const contain = Math.min(VIEW_W / w, viewH / h);
    setNatural({ w, h });
    setCoverScale(cover);
    setContainScale(contain);
    setZoom(cover);
    setPos(clampPos((VIEW_W - w * cover) / 2, (viewH - h * cover) / 2, cover, w, h));
  };

  const handlePointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos(clampPos(dragRef.current.origX + dx, dragRef.current.origY + dy, zoom));
  };
  const handlePointerUp = () => { dragRef.current = null; };

  const handleZoom = (e) => {
    const z = parseFloat(e.target.value);
    setZoom(z);
    setPos((p) => clampPos(p.x, p.y, z));
  };

  const handleConfirm = async () => {
    if (!natural.w) return;
    setBusy(true);
    try {
      const outH   = Math.round(OUTPUT_WIDTH / aspect);
      const scale  = OUTPUT_WIDTH / VIEW_W;
      const canvas = document.createElement('canvas');
      canvas.width  = OUTPUT_WIDTH;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        imgRef.current,
        0, 0, natural.w, natural.h,
        pos.x * scale, pos.y * scale, natural.w * zoom * scale, natural.h * zoom * scale,
      );
      // 여백은 흰색으로 채우지 않고 투명하게 둠 — PNG만 알파를 지원
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      onConfirm(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-crop-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="admin-crop-box">
        <h4 className="admin-crop-title">이미지 자르기</h4>

        <div
          className="admin-crop-viewport"
          style={{ width: VIEW_W, height: viewH }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            crossOrigin="anonymous"
            draggable={false}
            onLoad={handleImgLoad}
            className="admin-crop-img"
            style={{ left: pos.x, top: pos.y, width: natural.w * zoom, height: natural.h * zoom }}
          />
        </div>

        <div className="admin-crop-zoom-row">
          <span>축소</span>
          <input type="range" min={containScale} max={coverScale * 3} step={0.01} value={zoom} onChange={handleZoom} disabled={!natural.w} />
          <span>확대</span>
        </div>

        <div className="admin-crop-actions">
          <button type="button" className="admin-btn-ghost" onClick={onCancel}>취소</button>
          <button type="button" className="admin-btn-primary" onClick={handleConfirm} disabled={busy || !natural.w}>
            {busy ? '처리 중…' : '적용'}
          </button>
        </div>
      </div>
    </div>
  );
}
